import React, { useState, useEffect } from "react";
import { Box, Typography, Button, Container } from "@mui/material";
import { GlowingEffect } from "../ui/glowing-effect";

const SuccessStep = ({ onComplete }) => {
  const [isHovered, setIsHovered] = useState(false);
  const [showContent, setShowContent] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => {
      setShowContent(true);
    }, 200);
    return () => clearTimeout(timer);
  }, []);

  return (
    <Container 
      maxWidth={false}
      sx={{ 
        height: "100vh",
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
        position: "relative",
        px: { xs: 2, sm: 3, md: 4 },
        py: { xs: 4, sm: 6, md: 8 },
        overflow: "hidden",
        maxHeight: "100vh",
      }}
    >
      {/* Main content */}
      <Box
        sx={{
          textAlign: "center",
          maxWidth: "800px",
          width: "100%",
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          gap: { xs: 4, sm: 6 },
          height: "100%",
          maxHeight: "100%",
          overflow: "hidden",
        }}
      >
        {/* Success Message Card */}
        <Box
          sx={{
            position: "relative",
            width: "100%",
            borderRadius: "20px",
            border: "1px solid rgba(255, 255, 255, 0.1)",
            padding: "8px",
            animation: showContent ? "fadeInUp 0.6s cubic-bezier(0.16, 1, 0.3, 1) 0.2s both" : "none",
            "@keyframes fadeInUp": {
              "0%": {
                opacity: 0,
                transform: "translateY(24px)",
              },
              "100%": {
                opacity: 1,
                transform: "translateY(0)",
              },
            },
          }}
        >
          <GlowingEffect
            spread={32}
            glow={true}
            disabled={false}
            proximity={48}
            inactiveZone={0.01}
            borderWidth={1}
          />
          <Box
            sx={{
              position: "relative",
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              alignItems: "center",
              gap: 4,
              overflow: "hidden",
              borderRadius: "12px",
              padding: { xs: 4, sm: 5, md: 6 },
              background: "rgba(0, 0, 0, 0.4)",
              backdropFilter: "blur(10px)",
            }}
          >

            {/* Title */}
            <Typography
              variant="h1"
              component="h1"
              sx={{
                fontWeight: 700,
                color: "#ffffff",
                letterSpacing: "-0.04em",
                lineHeight: 1.1,
                fontSize: { xs: "1.5rem", sm: "2rem", md: "2.5rem" },
                fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', system-ui, sans-serif",
                mb: 2,
              }}
            >
              🎉 All Done!
            </Typography>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2, textAlign: "center", mb: 4 }}>
              <Typography
                variant="body1"
                sx={{
                  color: "#e2e8f0",
                  fontWeight: 400,
                  lineHeight: 1.6,
                  fontSize: { xs: "1rem", sm: "1.1rem" },
                  fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', system-ui, sans-serif",
                }}
              >
                You've done your part. It might take a few hours for your Playwright tests to be generated.
              </Typography>
              
              <Typography
                variant="body1"
                sx={{
                  color: "#e2e8f0",
                  fontWeight: 400,
                  lineHeight: 1.6,
                  fontSize: { xs: "1rem", sm: "1.1rem" },
                  fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', system-ui, sans-serif",
                }}
              >
                Once ready, a message will be sent over Slack and tests will be run twice a day by default.
              </Typography>
            </Box>

            {/* CTA Button */}
            <Box
              sx={{
                width: "100%",
                maxWidth: "320px",
              }}
            >
              <Button
                fullWidth
                onClick={onComplete}
                onMouseEnter={() => setIsHovered(true)}
                onMouseLeave={() => setIsHovered(false)}
                sx={{
                  height: 56,
                  borderRadius: 2,
                  background: "#ffffff",
                  border: "none",
                  color: "#000000",
                  fontSize: "1.1rem",
                  fontWeight: 600,
                  textTransform: "none",
                  fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', system-ui, sans-serif",
                  boxShadow: "0 4px 16px rgba(255, 255, 255, 0.2), 0 2px 8px rgba(255, 255, 255, 0.1)",
                  transition: "all 0.3s cubic-bezier(0.16, 1, 0.3, 1)",
                  "&:hover": {
                    transform: "translateY(-2px) scale(1.02)",
                    boxShadow: "0 8px 24px rgba(255, 255, 255, 0.25), 0 4px 12px rgba(255, 255, 255, 0.15)",
                    background: "#f8fafc",
                  },
                  "&:active": {
                    transform: "translateY(0) scale(1)",
                    transition: "all 0.1s cubic-bezier(0.16, 1, 0.3, 1)",
                  },
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 1.5,
                  }}
                >
                  <span>Go to Dashboard</span>
                  <Box
                    sx={{
                      transition: "transform 0.3s cubic-bezier(0.16, 1, 0.3, 1)",
                      transform: isHovered ? "translateX(4px)" : "translateX(0)",
                      fontSize: "1.2rem",
                    }}
                  >
                    →
                  </Box>
                </Box>
              </Button>
            </Box>
          </Box>
        </Box>
      </Box>
    </Container>
  );
};

export default SuccessStep; 